import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface StatCardProps {
  label: string;
  value: number | string;
  unit?: string;
  icon: LucideIcon;
  accent?: string;
  format?: "number" | "bytes" | "raw";
}

function useCountUp(target: number, duration = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (typeof target !== "number" || isNaN(target)) return;
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(target * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

function formatBytes(n: number): string {
  if (!isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function StatCard({
  label, value, unit, icon: Icon, accent = "text-accent-blue", format = "number",
}: StatCardProps) {
  const numeric = typeof value === "number" ? value : NaN;
  const animated = useCountUp(numeric);
  const display =
    typeof value === "string"
      ? value
      : format === "bytes"
      ? formatBytes(animated)
      : format === "raw"
      ? animated.toString()
      : Math.round(animated).toLocaleString();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className="glass glass-hover p-5 ring-glass relative overflow-hidden group hover:shadow-card-lift"
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background:
            "linear-gradient(135deg, rgba(96,165,250,0.08), rgba(167,139,250,0.04) 50%, transparent 75%)",
        }}
      />
      <div
        className="absolute inset-x-0 top-0 h-px opacity-60 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
        }}
      />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="eyebrow">{label}</div>
          <div className="mt-2 text-3xl font-bold font-sans tracking-tight gradient-text">
            {display}
            {unit && <span className="text-slate-500 text-lg ml-1 font-medium">{unit}</span>}
          </div>
        </div>
        <div
          className={`w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.10] flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] group-hover:border-white/[0.16] group-hover:bg-white/[0.07] transition-all ${accent}`}
        >
          <Icon size={18} strokeWidth={1.75} />
        </div>
      </div>
    </motion.div>
  );
}
